/**
 * WEB deep linking configuration — picked by the web bundler ahead of
 * linking.ts; Metro never sees it.
 *
 * React Navigation only honours URLs for screens that appear in the config
 * (with a config present, unlisted paths are ignored and the app starts at
 * its initial state, which is Splash). So every root and screen from the
 * route inventory gets `path = its name`: /LetterFirst/LettersHome,
 * /Dashboard/AllTasks, and so on — the same URLs the app already writes to
 * the address bar. Query strings become params (?letterId=…).
 *
 * A few roots get an initialRouteName so a deep link into the middle of a
 * flow has the flow's home screen beneath it and Back has somewhere to go.
 * The three email links keep their short paths. Whether the visitor may see
 * the screen is decided by the shell's deep-link guard, not here.
 */
import type { LinkingOptions, PathConfigMap } from '@react-navigation/native';

import routeInventory from '../../web/test/routeInventory.json';

const INITIAL_ROUTES: Record<string, string> = {
  LetterFirst: 'LettersHome',
  Documents: 'DocumentsDashboardScreen',
};

// Screens that take a `returnTo` object (where to go after an unlock or
// after setting the Recovery Code). Written to the URL as JSON so a reload
// keeps the way back; an unreadable value is simply dropped.
const RETURN_TO_SCREENS = new Set(['UnlockVault', 'SetRecoveryCodeIntro', 'SetRecoveryCodeInput', 'SetRecoveryCodeSuccess']);
const stringifyReturnTo = (value: unknown): string => (value == null ? '' : JSON.stringify(value));
const parseReturnTo = (raw: string): unknown => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && typeof parsed.root === 'string' ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const SPECIAL: Record<string, unknown> = {
  AcceptInvitation: {
    path: 'accept-invite',
    parse: { token: (token: string) => token },
  },
  FamilyInvite: {
    path: 'accept-invite/family',
    parse: { token: (token: string) => token },
  },
  EmailVerificationResult: {
    path: 'verify-email',
    parse: { token: (token: string) => token },
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const screens: PathConfigMap<any> = {};
const inventory = routeInventory as unknown as Record<string, string[] | null>;
for (const [root, names] of Object.entries(inventory)) {
  if (SPECIAL[root]) {
    screens[root] = SPECIAL[root];
    continue;
  }
  const nested: Record<string, unknown> = {};
  for (const name of names ?? []) {
    nested[name] = RETURN_TO_SCREENS.has(name)
      ? { path: name, stringify: { returnTo: stringifyReturnTo }, parse: { returnTo: parseReturnTo } }
      : name;
  }
  screens[root] = {
    path: root,
    ...(INITIAL_ROUTES[root] ? { initialRouteName: INITIAL_ROUTES[root] } : {}),
    screens: nested,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const linking: LinkingOptions<any> = {
  prefixes: [],
  config: { screens },
};
